import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api/client";
import { formatAgo } from "../lib/format";
import type { EffectiveLpsResponse } from "../types";

export interface EffectiveLpsView {
  result: EffectiveLpsResponse | null;
  pending: boolean;
  previous: boolean;
  reason: string | null;
}
interface Entry {
  address: string;
  generation: number;
  attempt: number;
  pending: boolean;
  settled: boolean;
  outcome: EffectiveLpsResponse | null;
  retained: EffectiveLpsResponse | null;
  reason: string | null;
  automaticRetries: number;
  retryAt?: number;
  retryTimer?: ReturnType<typeof setTimeout>;
  controller?: AbortController;
  listeners: Set<(view: EffectiveLpsView) => void>;
}

const unavailable = "Effective LP concentration is unavailable.";
const MAX_AUTOMATIC_RETRIES = 2;
const MIN_RETRY_DELAY_MS = 1_000;
const transientReasons = new Set([
  "queue_timeout",
  "queue_full",
  "rpc_lag",
  "rate_limited",
  "rpc_unavailable",
]);

export class EffectiveLpsCoordinator {
  readonly #entries = new Map<string, Entry>();
  readonly #request: (address: string, signal: AbortSignal) => Promise<EffectiveLpsResponse>;
  #disposed = false;
  #leases = 0;
  #leaseVersion = 0;

  constructor(
    request = (address: string, signal: AbortSignal) =>
      api<EffectiveLpsResponse>(`/pools/${address}/effective-lps`, { signal }),
  ) {
    this.#request = request;
  }

  lease(): () => void {
    this.#leases++;
    this.#leaseVersion++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#leases--;
      const version = ++this.#leaseVersion;
      queueMicrotask(() => {
        if (this.#leases === 0 && this.#leaseVersion === version) this.dispose();
      });
    };
  }

  subscribe(
    address: string,
    generation: number,
    listener: (view: EffectiveLpsView) => void,
  ): () => void {
    if (this.#disposed) return () => undefined;
    let entry = this.#entries.get(address);
    if (!entry) {
      this.#trim();
      if (this.#entries.size >= 256) {
        listener({ result: null, pending: false, previous: false, reason: unavailable });
        return () => undefined;
      }
      entry = {
        address,
        generation,
        attempt: 0,
        pending: false,
        settled: false,
        outcome: null,
        retained: null,
        reason: null,
        automaticRetries: 0,
        listeners: new Set(),
      };
      this.#entries.set(address, entry);
    } else if (generation > entry.generation) {
      this.#clearRetry(entry, true);
      entry.controller?.abort();
      entry.attempt++;
      entry.generation = generation;
      entry.pending = false;
      entry.settled = false;
      entry.reason = null;
      entry.automaticRetries = 0;
      if (entry.outcome?.status === "available") entry.retained = entry.outcome;
      entry.outcome = null;
    }
    entry.listeners.add(listener);
    listener(this.#view(entry));
    if (!entry.pending && !entry.settled) this.#start(entry);
    else if (!entry.pending && entry.settled) this.#scheduleRetry(entry);
    const subscribedEntry = entry;
    return () => {
      subscribedEntry.listeners.delete(listener);
      queueMicrotask(() => {
        if (
          !this.#disposed &&
          subscribedEntry.listeners.size === 0 &&
          (subscribedEntry.pending || subscribedEntry.retryTimer !== undefined)
        ) {
          this.#clearRetry(subscribedEntry, false);
          if (subscribedEntry.pending && !subscribedEntry.settled) {
            subscribedEntry.attempt++;
            subscribedEntry.pending = false;
            subscribedEntry.controller?.abort();
            subscribedEntry.controller = undefined;
          }
        }
      });
    };
  }

  dispose(): void {
    this.#disposed = true;
    for (const entry of this.#entries.values()) {
      this.#clearRetry(entry, true);
      entry.attempt++;
      entry.controller?.abort();
      entry.listeners.clear();
    }
    this.#entries.clear();
  }

  #start(entry: Entry): void {
    const controller = new AbortController();
    const attempt = ++entry.attempt;
    const generation = entry.generation;
    entry.controller = controller;
    entry.pending = true;
    this.#publish(entry);
    void this.#request(entry.address, controller.signal).then(
      (result) => {
        if (!this.#current(entry, attempt, generation)) return;
        entry.pending = false;
        entry.settled = true;
        entry.controller = undefined;
        entry.outcome = result;
        entry.reason = result.status === "unavailable" ? (result.reason ?? unavailable) : null;
        if (result.status === "available") entry.retained = result;
        this.#publish(entry);
        this.#scheduleRetry(entry);
      },
      () => {
        if (!this.#current(entry, attempt, generation) || controller.signal.aborted) return;
        entry.pending = false;
        entry.settled = true;
        entry.controller = undefined;
        entry.outcome = null;
        entry.reason = unavailable;
        this.#publish(entry);
      },
    );
  }

  #current(entry: Entry, attempt: number, generation: number): boolean {
    return (
      !this.#disposed &&
      this.#entries.get(entry.address) === entry &&
      entry.attempt === attempt &&
      entry.generation === generation
    );
  }

  #scheduleRetry(entry: Entry): void {
    if (
      this.#disposed ||
      entry.listeners.size === 0 ||
      entry.pending ||
      entry.retryTimer !== undefined ||
      entry.outcome?.status !== "unavailable" ||
      entry.automaticRetries >= MAX_AUTOMATIC_RETRIES ||
      !entry.outcome.reasonCode ||
      !transientReasons.has(entry.outcome.reasonCode) ||
      typeof entry.outcome.retryAfterMs !== "number" ||
      !Number.isFinite(entry.outcome.retryAfterMs) ||
      entry.outcome.retryAfterMs < 0
    )
      return;
    const generation = entry.generation;
    const due =
      entry.retryAt ?? Date.now() + Math.max(MIN_RETRY_DELAY_MS, entry.outcome.retryAfterMs);
    entry.retryAt = due;
    entry.retryTimer = setTimeout(
      () => {
        entry.retryTimer = undefined;
        if (
          !this.#disposed &&
          this.#entries.get(entry.address) === entry &&
          entry.generation === generation &&
          entry.listeners.size > 0
        ) {
          entry.retryAt = undefined;
          entry.automaticRetries++;
          entry.settled = false;
          this.#start(entry);
        }
      },
      Math.max(0, due - Date.now()),
    );
  }

  #clearRetry(entry: Entry, resetDue: boolean): void {
    if (entry.retryTimer !== undefined) clearTimeout(entry.retryTimer);
    entry.retryTimer = undefined;
    if (resetDue) entry.retryAt = undefined;
  }

  #view(entry: Entry): EffectiveLpsView {
    const current = entry.outcome?.status === "available" ? entry.outcome : null;
    const result = current ?? entry.retained ?? entry.outcome;
    return {
      result,
      pending: entry.pending,
      previous: current === null && entry.retained !== null,
      reason: entry.reason,
    };
  }

  #publish(entry: Entry): void {
    const view = this.#view(entry);
    for (const listener of entry.listeners) listener(view);
  }

  #trim(): void {
    if (this.#entries.size < 256) return;
    for (const [key, entry] of this.#entries) {
      if (entry.listeners.size !== 0) continue;
      entry.attempt++;
      this.#clearRetry(entry, true);
      entry.controller?.abort();
      this.#entries.delete(key);
      if (this.#entries.size < 256) return;
    }
  }
}

export function EffectiveLpsCell({
  address,
  coordinator,
  generation,
  now,
}: {
  address: string;
  coordinator: EffectiveLpsCoordinator;
  generation: number;
  now: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false);
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const [view, setView] = useState<EffectiveLpsView>({
    result: null,
    pending: false,
    previous: false,
    reason: null,
  });

  useEffect(() => {
    const row = root.current?.closest("tr");
    if (!row || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    });
    observer.observe(row);
    return () => {
      observer.disconnect();
    };
  }, [address]);
  useEffect(() => {
    if (!visible) return;
    return coordinator.subscribe(address, generation, setView);
  }, [address, coordinator, generation, visible]);

  const place = useCallback(() => {
    const anchor = trigger.current;
    const panel = tooltip.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    setPosition({
      left: Math.min(Math.max(8, a.right - p.width), Math.max(8, innerWidth - p.width - 8)),
      top:
        a.bottom + 6 + p.height <= innerHeight - 8
          ? a.bottom + 6
          : Math.max(8, a.top - p.height - 6),
    });
  }, []);
  useLayoutEffect(() => {
    if (shown) place();
  }, [place, shown]);
  useEffect(() => {
    if (!shown) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShown(false);
    };
    const dismissOutside = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node &&
        !trigger.current?.contains(target) &&
        !tooltip.current?.contains(target)
      )
        setShown(false);
    };
    addEventListener("keydown", dismiss);
    addEventListener("pointerdown", dismissOutside);
    addEventListener("resize", place);
    addEventListener("scroll", place, true);
    return () => {
      removeEventListener("keydown", dismiss);
      removeEventListener("pointerdown", dismissOutside);
      removeEventListener("resize", place);
      removeEventListener("scroll", place, true);
    };
  }, [place, shown]);

  const result = view.result;
  const available = result?.status === "available" && result.display !== null;
  const age = available && result.observedAt ? formatAgo(result.observedAt, now) : null;
  const previous = available && (view.previous || view.pending);
  const aged =
    available &&
    result.observedAt !== null &&
    Number.isFinite(Date.parse(result.observedAt)) &&
    now - Date.parse(result.observedAt) > 60_000;
  const reason = view.reason ?? result?.reason ?? unavailable;
  return (
    <div ref={root} className="effective-lps-cell">
      {!result && view.reason === null ? (
        <span className="effective-lps-loading" aria-label="Loading effective LPs" />
      ) : available ? (
        <span className="effective-lps-reading">
          <span className="num">{result.display}</span>
          {(previous || aged) && (
            <small>{`${previous ? "previous" : "aged"}${age ? ` · ${age}` : ""}`}</small>
          )}
        </span>
      ) : (
        <span
          className="unavailable"
          aria-label={`Effective LPs unavailable: ${reason}`}
          title={reason}
        >
          —
        </span>
      )}
      {available && (
        <button
          ref={trigger}
          type="button"
          className="effective-lps-info"
          aria-label="About effective LP concentration"
          aria-describedby={shown ? tooltipId : undefined}
          onFocus={() => {
            setShown(true);
          }}
          onBlur={() => {
            setShown(false);
          }}
          onPointerEnter={() => {
            setShown(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType !== "touch") setShown(false);
          }}
          onClick={() => {
            setShown(true);
          }}
        >
          i
        </button>
      )}
      {shown &&
        available &&
        createPortal(
          <div
            ref={tooltip}
            id={tooltipId}
            role="tooltip"
            className="effective-lps-tooltip"
            style={position}
          >
            <strong>{result.display} effective LPs</strong>
            <span>Equivalent number of equally sized active positions.</span>
            <span>
              {`${String(result.activePositions)} active ${result.activePositions === 1 ? "position" : "positions"} · largest share `}
              {result.largestLiquiditySharePercent === null
                ? "—"
                : `${result.largestLiquiditySharePercent}%`}
            </span>
            <span>{`Complete finalized snapshot · observed ${age ?? "at an unavailable time"}.`}</span>
            <span>Based on the snapshot price, not a live price or owner count.</span>
            {previous && (
              <span>
                {view.pending
                  ? "Refreshing; this is the previous snapshot."
                  : `Refresh unavailable; previous snapshot retained. ${reason}`}
              </span>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
